import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { apiRequest, shortDate } from '../../utils';

/* Their own engagement survey, brought in beside ours.

   "Keep the survey you have" is the first thing we say to a prospect, and this is the screen
   that makes it true. What lands here is a COMPANY-LEVEL figure — where the whole organisation
   stood on one day — never a per-person signal: counting somebody as engaged because their
   employer ran a survey would be inventing participation nobody observed.

   ★ The scale travels with the number. 7.4 is meaningless without knowing it came out of ten,
   so a reading can only be saved once a scale is picked, and the series is never drawn without
   it.

   ★ Re-importing a date overwrites that day rather than filing a second reading beside it, so
   the same export can be pasted twice without doubling anything. */

const SERVER_ERRORS = {
  ERR_NO_ENTRIES: 'extSurveyErrEmpty',
  ERR_TOO_MANY: 'extSurveyErrTooMany',
  ERR_BAD_DATE: 'extSurveyErrDate',
  ERR_BAD_SCORE: 'extSurveyErrScore',
  ERR_SCORE_OUT_OF_RANGE: 'extSurveyErrRange',
  ERR_FUTURE_DATE: 'extSurveyErrFuture',
  ERR_UNKNOWN_SCALE: 'extSurveyErrScale',
};

// "2026-03-31, 72" / "2026-03-31; 72" / "2026-03-31 72" — one reading per line. Parsed here only
// to catch an obviously empty box early; the server validates authoritatively and refuses the
// whole import rather than half of it.
const parseLines = (text) => (text || '')
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean)
  .map((line) => {
    const parts = line.split(/[,;\t ]+/).filter(Boolean);
    return { on: parts[0] || '', value: parts[1] };
  });

const ExternalSurvey = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [scale, setScale] = useState('');
  const [lines, setLines] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', '/company/external-survey')
      .then((res) => {
        setData(res);
        setName((res.meta && res.meta.name) || '');
        setScale((res.meta && res.meta.scale) || '');
      })
      .catch(() => setData({ points: [], meta: null, scales: [] }));
  }, []);

  useEffect(load, [load]);

  const save = async () => {
    const entries = parseLines(lines);
    if (!entries.length) { setError(t('extSurveyErrEmpty')); return; }
    if (!scale) { setError(t('extSurveyErrScale')); return; }
    setBusy(true); setError(null); setSaved(false);
    try {
      const res = await apiRequest('POST', '/company/external-survey', { name, scale, entries });
      setData(res);
      setLines('');
      setSaved(true);
    } catch (err) {
      // apiRequest carries the whole refused body on `error.data`, not on the Error itself.
      const code = (err && err.data && err.data.errors && err.data.errors.entries) || '';
      setError(t(SERVER_ERRORS[code] || 'extSurveyErrGeneric'));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const points = data.points || [];
  const scales = data.scales || [];

  return (
    <>
      <p className="bravos-q__hint">{t('extSurveyIntro')}</p>

      <div className="mb-3">
        <label className="form-label fw-bold" htmlFor="ext_name">{t('extSurveyName')}</label>
        <input id="ext_name" type="text" className="form-control" value={name}
               placeholder={t('extSurveyNamePlaceholder')}
               onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold" htmlFor="ext_scale">{t('extSurveyScale')}</label>
        <select id="ext_scale" className="form-select" value={scale}
                onChange={(e) => setScale(e.target.value)}>
          <option value="">{t('extSurveyScalePick')}</option>
          {scales.map((s) => (
            <option key={s} value={s}>{s === 'enps' ? t('extSurveyScaleEnps') : s}</option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold" htmlFor="ext_lines">{t('extSurveyLines')}</label>
        <textarea id="ext_lines" className="form-control" rows="4" value={lines}
                  placeholder={'2026-03-31, 72\n2025-09-30, 68'}
                  onChange={(e) => setLines(e.target.value)} />
        <div className="form-text">{t('extSurveyLinesHint')}</div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {saved && (
        <p className="text-success"><Icon name="check" className="me-1" />{t('extSurveySaved')}</p>
      )}

      <button type="button" className="bravos-submit" onClick={save} disabled={busy}>
        {busy ? <span className="auth__spinner" /> : <Icon name="upload" />} {t('extSurveySave')}
      </button>

      <hr className="my-4" />

      <h6>{t('extSurveyHistory')}</h6>
      {points.length === 0 ? (
        <p className="ce-note">{t('extSurveyNoPoints')}</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {points.slice().reverse().map((p) => (
            <li key={p.on} className="d-flex justify-content-between border-bottom py-2">
              <span>{shortDate(p.on, i18n.language)}</span>
              <strong>
                {p.value}
                {data.meta && data.meta.scale && data.meta.scale !== 'enps'
                  && <span className="text-muted"> / {data.meta.scale.split('-')[1]}</span>}
              </strong>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default ExternalSurvey;
